// src/component/ChatTab2026.js
//
// 2026 グループ活動 チャットタブ
//
// データ構造
//   groupChats2026/{groupId}/messages/{msgId}
//     { uid, name, avatar, text, mentions[], replyTo, likedBy[], deleted, createdAt }
//
// 機能
//   - 最新50件を onSnapshot、上スクロールで古い50件を getDocs で追加読み込み
//   - 返信（プレビュー保持） / 良いね（likedBy トグル） / 削除（自分 + isAdmin）
//   - @ メンション（仲間からサジェスト、ハイライト表示）
//   - URL リンク化（アクセントカラー）
//   - 画像投稿（jpg/png/heic）— Storage に full + thumb 2版アップロード、ライトボックスで全画面表示

import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
  getDocs,
  startAfter,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  deleteField,
  serverTimestamp,
  arrayUnion,
  arrayRemove,
} from "firebase/firestore";
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
} from "firebase/storage";
import { db, storage } from "../api/firebase";

import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";

import {
  Box,
  Stack,
  Typography,
  IconButton,
  Avatar,
  TextField,
  Menu,
  MenuItem,
  CircularProgress,
  Popper,
  Paper,
  ClickAwayListener,
  Tooltip,
} from "@mui/material";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import FavoriteBorderRoundedIcon from "@mui/icons-material/FavoriteBorderRounded";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import ChatBubbleOutlineRoundedIcon from "@mui/icons-material/ChatBubbleOutlineRounded";
import AddPhotoAlternateRoundedIcon from "@mui/icons-material/AddPhotoAlternateRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";

import { AppUserContext } from "./AppShell";

const PAGE_SIZE = 50;
const URL_RE = /(https?:\/\/[^\s]+)/g;
const MAX_TEXT_LEN = 2000;
const REPLY_PREVIEW_LEN = 80;
const IMAGE_FULL_MAX = 1400;
const IMAGE_FULL_TARGET_BYTES = 100 * 1024; // 100KB
const IMAGE_THUMB_MAX = 320;
const IMAGE_THUMB_TARGET_BYTES = 22 * 1024; // 約20KB
const IMAGE_MAX_INPUT_BYTES = 20 * 1024 * 1024; // 20MB（HEIC など）
const IMAGES_PER_MESSAGE = 4;
const CONSECUTIVE_GAP_MS = 5 * 60 * 1000; // 5分以内なら連続扱い

function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/* ──────────────────────────────────────────────────────────
   画像処理（HEIC変換 + リサイズ）
   ──────────────────────────────────────────────────────── */
function isHeic(file) {
  return (
    file.type === "image/heic" ||
    file.type === "image/heif" ||
    /\.hei[cf]$/i.test(file.name)
  );
}

function loadImageFromBlob(blob) {
  return new Promise((res, rej) => {
    const url = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => res({ img, url });
    img.onerror = (e) => {
      URL.revokeObjectURL(url);
      rej(e);
    };
    img.src = url;
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((res, rej) =>
    canvas.toBlob((b) => (b ? res(b) : rej(new Error("toBlob failed"))), type, quality)
  );
}

function renderToBlob(img, maxDim, quality) {
  const ratio = Math.min(1, maxDim / Math.max(img.naturalWidth, img.naturalHeight));
  const w = Math.max(1, Math.round(img.naturalWidth * ratio));
  const h = Math.max(1, Math.round(img.naturalHeight * ratio));
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);
  return canvasToBlob(canvas, "image/jpeg", quality).then((blob) => ({
    blob,
    width: w,
    height: h,
  }));
}

// 目標バイト数以下に収めるため品質→寸法の順で段階的に圧縮
async function compressToTarget(img, startMaxDim, targetBytes) {
  const qualities = [0.8, 0.7, 0.6, 0.5, 0.4];
  const dims = [startMaxDim, Math.round(startMaxDim * 0.85), Math.round(startMaxDim * 0.7)];
  let best = null;
  for (const dim of dims) {
    for (const q of qualities) {
      const out = await renderToBlob(img, dim, q);
      if (!best || out.blob.size < best.blob.size) best = out;
      if (out.blob.size <= targetBytes) return out;
    }
  }
  return best;
}

async function processImageFile(file) {
  if (file.size > IMAGE_MAX_INPUT_BYTES) {
    throw new Error("ファイルが大きすぎます（20MB以下にしてください）");
  }
  let working = file;
  if (isHeic(file)) {
    const heic2any = (await import("heic2any")).default;
    const converted = await heic2any({
      blob: file,
      toType: "image/jpeg",
      quality: 0.9,
    });
    working = Array.isArray(converted) ? converted[0] : converted;
  }
  const { img, url } = await loadImageFromBlob(working);
  try {
    const full = await compressToTarget(img, IMAGE_FULL_MAX, IMAGE_FULL_TARGET_BYTES);
    const thumb = await compressToTarget(img, IMAGE_THUMB_MAX, IMAGE_THUMB_TARGET_BYTES);
    return {
      full,
      thumb,
      width: img.naturalWidth,
      height: img.naturalHeight,
      previewUrl: URL.createObjectURL(thumb.blob),
    };
  } finally {
    URL.revokeObjectURL(url);
  }
}

async function uploadImageBlob(groupId, sub, blob) {
  const key = `${Date.now()}_${Math.random().toString(36).slice(2, 8)}_${sub}.jpg`;
  const r = storageRef(storage, `groupChats2026/${groupId}/images/${key}`);
  await uploadBytes(r, blob, { contentType: "image/jpeg" });
  return getDownloadURL(r);
}

function formatTime(ts) {
  if (!ts?.toDate) return "";
  const d = ts.toDate();
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  if (sameDay) return `${hh}:${mm}`;
  return `${d.getMonth() + 1}/${d.getDate()} ${hh}:${mm}`;
}

/* ──────────────────────────────────────────────────────────
   リッチテキスト描画（URL + メンション）
   ──────────────────────────────────────────────────────── */
function renderRichText(text, members, accent) {
  if (!text) return null;

  const memberNames = (members || [])
    .map((m) => m.name)
    .filter(Boolean)
    .sort((a, b) => b.length - a.length); // 長い名前を優先マッチ
  const mentionRe = memberNames.length
    ? new RegExp(`@(${memberNames.map(escapeRegex).join("|")})`, "g")
    : null;

  // まず URL でセグメント分割
  const urlSegments = [];
  let last = 0;
  let m;
  const urlRe = new RegExp(URL_RE.source, "g");
  while ((m = urlRe.exec(text)) !== null) {
    if (m.index > last) urlSegments.push({ kind: "text", value: text.slice(last, m.index) });
    urlSegments.push({ kind: "url", value: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) urlSegments.push({ kind: "text", value: text.slice(last) });

  // text セグメントをさらに mention で分割
  const out = [];
  urlSegments.forEach((seg, i) => {
    if (seg.kind === "url") {
      out.push(
        <a
          key={`u-${i}`}
          href={seg.value}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: accent, textDecoration: "underline", wordBreak: "break-all" }}
        >
          {seg.value}
        </a>
      );
      return;
    }
    if (!mentionRe) {
      out.push(<React.Fragment key={`t-${i}`}>{seg.value}</React.Fragment>);
      return;
    }
    const re = new RegExp(mentionRe.source, "g");
    let last2 = 0;
    let mm;
    let part = 0;
    while ((mm = re.exec(seg.value)) !== null) {
      if (mm.index > last2)
        out.push(
          <React.Fragment key={`t-${i}-${part++}`}>
            {seg.value.slice(last2, mm.index)}
          </React.Fragment>
        );
      out.push(
        <span
          key={`m-${i}-${part++}`}
          style={{ color: accent, fontWeight: 700 }}
        >
          {mm[0]}
        </span>
      );
      last2 = mm.index + mm[0].length;
    }
    if (last2 < seg.value.length)
      out.push(
        <React.Fragment key={`t-${i}-${part++}`}>
          {seg.value.slice(last2)}
        </React.Fragment>
      );
  });

  return out;
}

/* ──────────────────────────────────────────────────────────
   1メッセージのバブル
   ──────────────────────────────────────────────────────── */
function MessageBubble({
  msg,
  members,
  currentUid,
  isAdmin,
  accent,
  textBase,
  border,
  surface,
  onReply,
  onToggleLike,
  onDelete,
  onOpenImage,
  onImageLoaded,
  tappedActions,
  onToggleTap,
  isConsecutive,
}) {
  const isOwn = msg.uid === currentUid;
  const liked = Array.isArray(msg.likedBy) && msg.likedBy.includes(currentUid);
  const likeCount = msg.likedBy?.length || 0;
  const canDelete = !msg.deleted && (isOwn || isAdmin);

  const [menuAnchor, setMenuAnchor] = useState(null);
  const longPressTimerRef = useRef(null);
  const longPressFiredRef = useRef(false);

  if (msg.deleted) {
    return (
      <Box sx={{ display: "flex", justifyContent: isOwn ? "flex-end" : "flex-start", my: 0.5 }}>
        <Typography
          variant="caption"
          sx={{ color: textBase, opacity: 0.45, fontStyle: "italic", px: 1.5 }}
        >
          メッセージは削除されました
        </Typography>
      </Box>
    );
  }

  const images = Array.isArray(msg.images)
    ? msg.images.filter((i) => i?.url)
    : msg.image?.url
    ? [msg.image]
    : [];
  const hasImage = images.length > 0;
  const hasText = !!(msg.text && msg.text.trim());
  const singleAspect =
    hasImage && images.length === 1 && images[0].width && images[0].height
      ? `${images[0].width} / ${images[0].height}`
      : undefined;

  const actionsRevealed = tappedActions;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isOwn ? "row-reverse" : "row",
        alignItems: "flex-end",
        gap: 1,
        mt: isConsecutive ? 0.25 : 0.75,
        mb: 0.25,
        "@media (hover: hover)": {
          "&:hover .msg-actions": { opacity: 1, pointerEvents: "auto" },
        },
      }}
    >
      {isConsecutive ? (
        <Box sx={{ width: 32, flexShrink: 0 }} />
      ) : (
        <Avatar
          src={msg.avatar || undefined}
          alt={msg.name || ""}
          sx={{ width: 32, height: 32, bgcolor: `${accent}20`, fontSize: 14 }}
        >
          {(msg.name || "?").slice(0, 1)}
        </Avatar>
      )}

      <Box
        sx={{
          maxWidth: "78%",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: isOwn ? "flex-end" : "flex-start",
        }}
      >
        {!isConsecutive && (
          <Stack
            direction={isOwn ? "row-reverse" : "row"}
            spacing={0.75}
            alignItems="center"
            sx={{ mb: 0.25, px: 0.5 }}
          >
            <Typography
              variant="caption"
              sx={{ fontWeight: 700, color: textBase, opacity: 0.85 }}
            >
              {msg.name || "名前未設定"}
            </Typography>
            <Typography variant="caption" sx={{ color: textBase, opacity: 0.45 }}>
              {formatTime(msg.createdAt)}
            </Typography>
          </Stack>
        )}

        {/* バブル + サイドアクション */}
        <Box
          sx={{
            position: "relative",
            display: "inline-flex",
            maxWidth: "100%",
          }}
        >
          <Box
            onClick={(e) => {
              e.stopPropagation();
              onToggleTap(msg.id);
            }}
            sx={{
              px: hasImage && !hasText && !msg.replyTo ? 0.5 : 1.5,
              py: hasImage && !hasText && !msg.replyTo ? 0.5 : 1,
              borderRadius: 2,
              bgcolor: isOwn ? `${accent}14` : surface,
              border: `1px solid ${border}`,
              wordBreak: "break-word",
              cursor: "pointer",
              minWidth: 0,
              maxWidth: "100%",
            }}
          >
            {msg.replyTo && (
              <Box
                sx={{
                  mb: 0.75,
                  pl: 1,
                  borderLeft: `3px solid ${accent}`,
                  opacity: 0.75,
                }}
              >
                <Typography variant="caption" sx={{ color: accent, fontWeight: 700 }}>
                  {msg.replyTo.name || "返信"}
                </Typography>
                <Typography
                  variant="caption"
                  component="div"
                  sx={{
                    color: textBase,
                    opacity: 0.8,
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                  }}
                >
                  {msg.replyTo.textPreview || ""}
                </Typography>
              </Box>
            )}

            {hasImage && (
              <Box
                sx={{
                  display: "grid",
                  gap: 0.5,
                  width: "100%",
                  maxWidth: 280,
                  mb: hasText ? 0.75 : 0,
                  gridTemplateColumns:
                    images.length === 1
                      ? "1fr"
                      : images.length === 3
                      ? "1fr 1fr 1fr"
                      : "1fr 1fr",
                }}
              >
                {images.map((img, idx) => {
                  const startLongPress = () => {
                    longPressFiredRef.current = false;
                    if (longPressTimerRef.current) clearTimeout(longPressTimerRef.current);
                    longPressTimerRef.current = setTimeout(() => {
                      longPressFiredRef.current = true;
                      onToggleTap(msg.id);
                      longPressTimerRef.current = null;
                    }, 500);
                  };
                  const cancelLongPress = () => {
                    if (longPressTimerRef.current) {
                      clearTimeout(longPressTimerRef.current);
                      longPressTimerRef.current = null;
                    }
                  };
                  return (
                    <Box
                      key={idx}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (longPressFiredRef.current) {
                          longPressFiredRef.current = false;
                          return;
                        }
                        onOpenImage(msg, idx);
                      }}
                      onPointerDown={startLongPress}
                      onPointerUp={cancelLongPress}
                      onPointerLeave={cancelLongPress}
                      onPointerCancel={cancelLongPress}
                      onContextMenu={(e) => e.preventDefault()}
                      sx={{
                        borderRadius: 1.5,
                        overflow: "hidden",
                        bgcolor: `${accent}10`,
                        aspectRatio: images.length === 1 ? singleAspect : "1 / 1",
                        cursor: "zoom-in",
                        WebkitTouchCallout: "none",
                        WebkitUserSelect: "none",
                        userSelect: "none",
                      }}
                    >
                      <Box
                        component="img"
                        src={img.thumbUrl || img.url}
                        alt=""
                        loading="lazy"
                        draggable={false}
                        onLoad={onImageLoaded}
                        sx={{
                          display: "block",
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                          pointerEvents: "none",
                        }}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}

            {hasText && (
              <Typography
                variant="body2"
                sx={{ color: textBase, whiteSpace: "pre-wrap", lineHeight: 1.55 }}
              >
                {renderRichText(msg.text, members, accent)}
              </Typography>
            )}
          </Box>

          {/* アクション（デスクトップ:横 / モバイル:上） */}
          <Stack
            className="msg-actions"
            direction="row"
            spacing={0.25}
            alignItems="center"
            sx={{
              position: "absolute",
              // デスクトップ: バブルの横（hover 可・ポインタ細）
              top: "50%",
              transform: "translateY(-50%)",
              [isOwn ? "right" : "left"]: "100%",
              [isOwn ? "mr" : "ml"]: 0.5,
              // モバイル（タッチ端末）: バブルの上に切り替え
              "@media (hover: none) and (pointer: coarse)": {
                top: "auto",
                transform: "none",
                bottom: "calc(100% + 4px)",
                [isOwn ? "right" : "left"]: 0,
                [isOwn ? "mr" : "ml"]: 0,
              },
              opacity: actionsRevealed ? 1 : 0,
              pointerEvents: actionsRevealed ? "auto" : "none",
              transition: "opacity 0.15s",
              bgcolor: surface,
              border: `1px solid ${border}`,
              borderRadius: 999,
              px: 0.25,
              py: 0.25,
              whiteSpace: "nowrap",
              zIndex: 2,
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <Tooltip title={liked ? "いいねを取り消す" : "いいね"}>
              <IconButton size="small" onClick={() => onToggleLike(msg)} sx={{ p: 0.5 }}>
                {liked ? (
                  <FavoriteRoundedIcon sx={{ fontSize: 16, color: accent }} />
                ) : (
                  <FavoriteBorderRoundedIcon
                    sx={{ fontSize: 16, color: textBase, opacity: 0.55 }}
                  />
                )}
              </IconButton>
            </Tooltip>
            <Tooltip title="返信">
              <IconButton size="small" onClick={() => onReply(msg)} sx={{ p: 0.5 }}>
                <ReplyRoundedIcon sx={{ fontSize: 16, color: textBase, opacity: 0.55 }} />
              </IconButton>
            </Tooltip>
            {canDelete && (
              <>
                <IconButton
                  size="small"
                  onClick={(e) => setMenuAnchor(e.currentTarget)}
                  sx={{ p: 0.5 }}
                >
                  <MoreVertIcon sx={{ fontSize: 16, color: textBase, opacity: 0.55 }} />
                </IconButton>
                <Menu
                  anchorEl={menuAnchor}
                  open={Boolean(menuAnchor)}
                  onClose={() => setMenuAnchor(null)}
                >
                  <MenuItem
                    onClick={() => {
                      setMenuAnchor(null);
                      onDelete(msg);
                    }}
                    sx={{ color: "#c62828", fontSize: 14 }}
                  >
                    削除
                  </MenuItem>
                </Menu>
              </>
            )}
          </Stack>
        </Box>

        {/* いいね数（バブル下にコンパクトに） */}
        {likeCount > 0 && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.25}
            sx={{ mt: 0.25, px: 0.5 }}
          >
            <FavoriteRoundedIcon sx={{ fontSize: 12, color: accent }} />
            <Typography variant="caption" sx={{ color: textBase, opacity: 0.65 }}>
              {likeCount}
            </Typography>
          </Stack>
        )}
      </Box>
    </Box>
  );
}

/* ──────────────────────────────────────────────────────────
   メイン
   ──────────────────────────────────────────────────────── */
export default function ChatTab2026({
  groupId,
  members,
  accent,
  textBase,
  border,
  surface,
}) {
  const { userInfo, isAdmin } = useContext(AppUserContext) || {
    userInfo: null,
    isAdmin: false,
  };
  const currentUid = userInfo?.id || null;
  const currentName = userInfo?.name || "";
  const currentAvatar = userInfo?.avatar || "";

  // 新着50件（subscription）と過去ロード分（pagination）を分けて保持
  const [liveMsgs, setLiveMsgs] = useState([]); // 昇順
  const [olderMsgs, setOlderMsgs] = useState([]); // 昇順（追加ロード分）
  const [loadingInitial, setLoadingInitial] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const oldestDocRef = useRef(null); // pagination cursor（QueryDocumentSnapshot）

  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [sending, setSending] = useState(false);

  const [mentionAnchor, setMentionAnchor] = useState(null);
  const [mentionQuery, setMentionQuery] = useState("");

  const [tappedActionsId, setTappedActionsId] = useState(null);
  const [pendingImages, setPendingImages] = useState([]); // [{ full, thumb, width, height, previewUrl }]
  const [processingImage, setProcessingImage] = useState(false);
  const [imageError, setImageError] = useState("");
  const [lightboxIndex, setLightboxIndex] = useState(-1);

  const containerRef = useRef(null);
  const listRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const prevLiveCountRef = useRef(0);

  // チャットコンテナの高さを動的に計算（上端 → 下ナビ上端まで自動で埋める）
  const [containerHeight, setContainerHeight] = useState(null);
  useLayoutEffect(() => {
    const BOTTOM_NAV_PX = 56; // AppShell の BottomNavigation
    const BOTTOM_GAP = 8;
    const measure = () => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const vh = window.visualViewport?.height ?? window.innerHeight;
      const available = vh - rect.top - BOTTOM_NAV_PX - BOTTOM_GAP;
      setContainerHeight(Math.max(320, Math.round(available)));
    };
    measure();
    const t = setTimeout(measure, 250); // 画像/フォント読込でレイアウト変動した時用
    window.addEventListener("resize", measure);
    window.visualViewport?.addEventListener("resize", measure);
    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", measure);
      window.visualViewport?.removeEventListener("resize", measure);
    };
  }, []);

  // タッチデバイス判定（hover不可 & ポインタが coarse）— スマホ/タブレットで true
  const isTouchDevice = useMemo(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(hover: none) and (pointer: coarse)").matches;
  }, []);

  /* 新着 subscription */
  useEffect(() => {
    if (!groupId) return undefined;
    setLoadingInitial(true);
    setOlderMsgs([]);
    oldestDocRef.current = null;
    setHasMore(true);
    const colRef = collection(db, "groupChats2026", groupId, "messages");
    const q = query(colRef, orderBy("createdAt", "desc"), limit(PAGE_SIZE));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const docs = snap.docs;
        const arr = docs.map((d) => ({ id: d.id, ...d.data() })).reverse(); // 昇順
        setLiveMsgs(arr);
        if (docs.length > 0) {
          oldestDocRef.current = docs[docs.length - 1];
        }
        if (docs.length < PAGE_SIZE) setHasMore(false);
        setLoadingInitial(false);
      },
      (err) => {
        console.error("chat subscription error", err);
        setLoadingInitial(false);
      }
    );
    return () => unsub();
  }, [groupId]);

  /* 古いメッセージのロード */
  const loadOlder = useCallback(async () => {
    if (!groupId || loadingMore || !hasMore || !oldestDocRef.current) return;
    setLoadingMore(true);
    const listEl = listRef.current;
    const prevScrollHeight = listEl?.scrollHeight || 0;
    const prevScrollTop = listEl?.scrollTop || 0;
    try {
      const colRef = collection(db, "groupChats2026", groupId, "messages");
      const q = query(
        colRef,
        orderBy("createdAt", "desc"),
        startAfter(oldestDocRef.current),
        limit(PAGE_SIZE)
      );
      const snap = await getDocs(q);
      const docs = snap.docs;
      const arr = docs.map((d) => ({ id: d.id, ...d.data() })).reverse();
      if (docs.length > 0) {
        oldestDocRef.current = docs[docs.length - 1];
        setOlderMsgs((prev) => [...arr, ...prev]);
      }
      if (docs.length < PAGE_SIZE) setHasMore(false);

      // スクロール位置を保持
      requestAnimationFrame(() => {
        const el = listRef.current;
        if (!el) return;
        const newScrollHeight = el.scrollHeight;
        el.scrollTop = prevScrollTop + (newScrollHeight - prevScrollHeight);
      });
    } catch (e) {
      console.error("load older error", e);
    } finally {
      setLoadingMore(false);
    }
  }, [groupId, loadingMore, hasMore]);

  /* スクロール監視（上端でロード、最下端追従） */
  const onListScroll = useCallback(
    (e) => {
      const el = e.currentTarget;
      if (el.scrollTop < 80 && hasMore && !loadingMore && !loadingInitial) {
        loadOlder();
      }
      const distFromBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
      stickToBottomRef.current = distFromBottom < 80;
    },
    [hasMore, loadingMore, loadingInitial, loadOlder]
  );

  /* 新着で最下端追従 / 初回ロード時は最新を表示 */
  useEffect(() => {
    const newCount = liveMsgs.length;
    const prev = prevLiveCountRef.current;
    prevLiveCountRef.current = newCount;
    if (!listRef.current) return;
    const scrollToBottom = () => {
      const el = listRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    };
    if (prev === 0 && newCount > 0) {
      // 初回ロード: 画像の遅延ロードで高さが伸びる分も追従するよう複数回スクロール
      requestAnimationFrame(scrollToBottom);
      const t1 = setTimeout(scrollToBottom, 100);
      const t2 = setTimeout(scrollToBottom, 400);
      const t3 = setTimeout(scrollToBottom, 1000);
      return () => {
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
      };
    }
    if (stickToBottomRef.current) {
      requestAnimationFrame(scrollToBottom);
    }
    return undefined;
  }, [liveMsgs]);

  /* メンバー uid → 最新表示名 のルックアップ
     用途: 返信プレビューに返信時点のターゲット最新名を埋める */
  const nameByUid = useMemo(() => {
    const map = {};
    (members || []).forEach((m) => {
      if (m.uid && m.name) map[m.uid] = m.name;
    });
    return map;
  }, [members]);

  /* メンション候補 */
  const mentionCandidates = useMemo(() => {
    if (mentionAnchor === null) return [];
    const q = (mentionQuery || "").toLowerCase();
    return (members || [])
      .filter((m) => m.uid && m.name)
      .filter((m) => !q || m.name.toLowerCase().includes(q))
      .slice(0, 6);
  }, [members, mentionQuery, mentionAnchor]);

  /* テキスト変更 — 末尾の @xxx を検知してメンション候補オープン */
  const handleTextChange = (e) => {
    const v = e.target.value;
    setText(v);
    // カーソル位置直前の @xxx を探す
    const caret = e.target.selectionStart ?? v.length;
    const before = v.slice(0, caret);
    const m = before.match(/@([^\s@]*)$/);
    if (m) {
      setMentionAnchor(inputRef.current);
      setMentionQuery(m[1]);
    } else {
      setMentionAnchor(null);
      setMentionQuery("");
    }
  };

  const insertMention = (member) => {
    const el = inputRef.current?.querySelector("textarea, input");
    const caret = el?.selectionStart ?? text.length;
    const before = text.slice(0, caret);
    const after = text.slice(caret);
    const replaced = before.replace(/@([^\s@]*)$/, `@${member.name} `);
    const newText = replaced + after;
    setText(newText);
    setMentionAnchor(null);
    setMentionQuery("");
    requestAnimationFrame(() => {
      const el2 = inputRef.current?.querySelector("textarea, input");
      if (el2) {
        const pos = replaced.length;
        el2.focus();
        el2.setSelectionRange(pos, pos);
      }
    });
  };

  /* 画像ファイル選択 */
  const handlePickFile = () => {
    setImageError("");
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // 同じファイル再選択可
    if (files.length === 0) return;

    const remainingSlots = IMAGES_PER_MESSAGE - pendingImages.length;
    if (remainingSlots <= 0) {
      setImageError(`画像は最大 ${IMAGES_PER_MESSAGE} 枚までです`);
      return;
    }
    const accepted = files.filter(
      (f) => f.type === "image/jpeg" || f.type === "image/png" || isHeic(f)
    );
    if (accepted.length === 0) {
      setImageError("JPG / PNG / HEIC のみ送信できます");
      return;
    }
    const toProcess = accepted.slice(0, remainingSlots);
    const overflow =
      files.length > toProcess.length || accepted.length > toProcess.length;

    setProcessingImage(true);
    setImageError("");
    try {
      const results = [];
      for (const file of toProcess) {
        // eslint-disable-next-line no-await-in-loop
        const r = await processImageFile(file);
        results.push(r);
      }
      setPendingImages((prev) => [...prev, ...results]);
      if (overflow) {
        setImageError(
          `画像は最大 ${IMAGES_PER_MESSAGE} 枚まで（${toProcess.length} 枚を追加しました）`
        );
      }
    } catch (err) {
      console.error("image processing error", err);
      setImageError(err?.message || "画像の処理に失敗しました");
    } finally {
      setProcessingImage(false);
    }
  };

  const removePendingImage = (index) => {
    setPendingImages((prev) => {
      const target = prev[index];
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((_, i) => i !== index);
    });
  };

  const clearAllPendingImages = () => {
    pendingImages.forEach((p) => {
      if (p?.previewUrl) URL.revokeObjectURL(p.previewUrl);
    });
    setPendingImages([]);
  };

  /* 送信 */
  const handleSend = async () => {
    if (sending || processingImage) return;
    const t = text.trim();
    if (!t && pendingImages.length === 0) return;
    if (!currentUid || !groupId) return;
    if (t.length > MAX_TEXT_LEN) return;

    // メンション uid 抽出
    const memberNames = (members || [])
      .filter((m) => m.uid && m.name)
      .sort((a, b) => b.name.length - a.name.length);
    const mentions = [];
    memberNames.forEach((m) => {
      const re = new RegExp(`@${escapeRegex(m.name)}(?=\\s|$)`, "g");
      if (re.test(t) && !mentions.includes(m.uid)) mentions.push(m.uid);
    });

    setSending(true);
    try {
      let imagesField = [];
      if (pendingImages.length > 0) {
        imagesField = await Promise.all(
          pendingImages.map(async (p) => {
            const [fullUrl, thumbUrl] = await Promise.all([
              uploadImageBlob(groupId, "full", p.full.blob),
              uploadImageBlob(groupId, "thumb", p.thumb.blob),
            ]);
            return {
              url: fullUrl,
              thumbUrl,
              width: p.full.width,
              height: p.full.height,
            };
          })
        );
      }

      // /my-page で更新された名前・アバターを反映するため、送信直前に
      // users/{uid} を再読込してスタンプする（cached userInfo は古い場合がある）
      let stampName = currentName;
      let stampAvatar = currentAvatar;
      try {
        const userSnap = await getDoc(doc(db, "users", currentUid));
        if (userSnap.exists()) {
          const d = userSnap.data();
          if (d.name) stampName = d.name;
          if (typeof d.avatar === "string") stampAvatar = d.avatar;
        }
      } catch (e) {
        console.warn("send: live user fetch failed, falling back to cache", e);
      }

      const colRef = collection(db, "groupChats2026", groupId, "messages");
      await addDoc(colRef, {
        uid: currentUid,
        name: stampName,
        avatar: stampAvatar,
        text: t,
        mentions,
        replyTo: replyTo
          ? {
              id: replyTo.id,
              uid: replyTo.uid || null,
              name: replyTo.name || "",
              textPreview: (replyTo.text || "").slice(0, REPLY_PREVIEW_LEN),
            }
          : null,
        images: imagesField,
        likedBy: [],
        deleted: false,
        createdAt: serverTimestamp(),
      });
      setText("");
      setReplyTo(null);
      clearAllPendingImages();
      stickToBottomRef.current = true;
    } catch (e) {
      console.error("send message error", e);
      setImageError("送信に失敗しました");
    } finally {
      setSending(false);
      requestAnimationFrame(() => {
        const el = inputRef.current?.querySelector("textarea, input");
        el?.focus();
      });
    }
  };

  const handleKeyDown = (e) => {
    if (mentionAnchor && mentionCandidates.length > 0 && e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      insertMention(mentionCandidates[0]);
      return;
    }
    // スマホ（タッチデバイス）では Enter は常に改行、送信は送信ボタンのみ
    if (isTouchDevice) return;
    if (e.key === "Enter" && !e.shiftKey && !e.isComposing && !e.nativeEvent?.isComposing) {
      e.preventDefault();
      handleSend();
    }
  };

  /* いいねトグル */
  const handleToggleLike = async (msg) => {
    if (!currentUid || !groupId) return;
    const ref = doc(db, "groupChats2026", groupId, "messages", msg.id);
    const liked = Array.isArray(msg.likedBy) && msg.likedBy.includes(currentUid);
    try {
      await updateDoc(ref, {
        likedBy: liked ? arrayRemove(currentUid) : arrayUnion(currentUid),
      });
      // 古いメッセージは subscription 範囲外なので楽観更新
      setOlderMsgs((prev) =>
        prev.map((m) =>
          m.id === msg.id
            ? {
                ...m,
                likedBy: liked
                  ? (m.likedBy || []).filter((u) => u !== currentUid)
                  : [...(m.likedBy || []), currentUid],
              }
            : m
        )
      );
    } catch (e) {
      console.error("toggle like error", e);
    }
  };

  /* 削除（ソフトデリート） */
  const handleDelete = async (msg) => {
    if (!groupId) return;
    if (!(msg.uid === currentUid || isAdmin)) return;
    const ref = doc(db, "groupChats2026", groupId, "messages", msg.id);
    try {
      await updateDoc(ref, {
        deleted: true,
        deletedAt: serverTimestamp(),
        deletedBy: currentUid,
        text: deleteField(),
      });
      setOlderMsgs((prev) =>
        prev.map((m) => (m.id === msg.id ? { ...m, deleted: true, text: "" } : m))
      );
    } catch (e) {
      console.error("delete message error", e);
    }
  };

  const handleReply = (msg) => {
    // 返信プレビューには返信時点でのターゲットの最新名を使う
    const latestName = (msg.uid && nameByUid[msg.uid]) || msg.name || "";
    setReplyTo({
      id: msg.id,
      uid: msg.uid || null,
      name: latestName,
      text: msg.text,
    });
    setTappedActionsId(null);
    const el = inputRef.current?.querySelector("textarea, input");
    el?.focus();
  };

  const handleToggleTap = useCallback((id) => {
    setTappedActionsId((prev) => (prev === id ? null : id));
  }, []);

  // 画像の遅延ロードで高さが伸びた時、最下端にいたなら追従
  const handleImageLoaded = useCallback(() => {
    if (!stickToBottomRef.current) return;
    const el = listRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, []);

  const allMessages = useMemo(() => [...olderMsgs, ...liveMsgs], [olderMsgs, liveMsgs]);

  /* ライトボックス用の画像一覧（メッセージ順、すべての画像をフラット化） */
  const imageSlides = useMemo(() => {
    const slides = [];
    allMessages.forEach((m) => {
      if (m.deleted) return;
      const imgs = Array.isArray(m.images)
        ? m.images
        : m.image
        ? [m.image]
        : [];
      imgs.forEach((img, i) => {
        if (!img?.url) return;
        slides.push({
          key: `${m.id}_${i}`,
          msgId: m.id,
          imgIdx: i,
          uid: m.uid,
          src: img.url,
          width: img.width,
          height: img.height,
        });
      });
    });
    return slides;
  }, [allMessages]);

  const handleOpenImage = useCallback(
    (msg, imgIdx = 0) => {
      const idx = imageSlides.findIndex((s) => s.key === `${msg.id}_${imgIdx}`);
      if (idx >= 0) setLightboxIndex(idx);
    },
    [imageSlides]
  );

  /* ライトボックスから1枚だけ削除 */
  const handleDeleteImageAt = useCallback(
    async (slideIndex) => {
      const slide = imageSlides[slideIndex];
      if (!slide || !groupId) return;
      const msg = allMessages.find((m) => m.id === slide.msgId);
      if (!msg) return;
      if (!(msg.uid === currentUid || isAdmin)) return;
      // eslint-disable-next-line no-alert
      if (!window.confirm("この画像を削除します。よろしいですか？")) return;

      const images = Array.isArray(msg.images)
        ? msg.images.slice()
        : msg.image
        ? [msg.image]
        : [];
      const newImages = images.filter((_, i) => i !== slide.imgIdx);
      const hasText = !!(msg.text && msg.text.trim());
      const ref = doc(db, "groupChats2026", groupId, "messages", msg.id);

      try {
        if (newImages.length === 0 && !hasText) {
          await updateDoc(ref, {
            deleted: true,
            deletedAt: serverTimestamp(),
            deletedBy: currentUid,
            text: deleteField(),
            images: [],
          });
        } else {
          await updateDoc(ref, { images: newImages });
        }
        setOlderMsgs((prev) =>
          prev.map((m) => {
            if (m.id !== msg.id) return m;
            if (newImages.length === 0 && !hasText) {
              return { ...m, deleted: true, text: "", images: [] };
            }
            return { ...m, images: newImages };
          })
        );
        setLightboxIndex(-1);
      } catch (e) {
        console.error("delete image error", e);
      }
    },
    [imageSlides, allMessages, currentUid, isAdmin, groupId]
  );

  /* 画像 previewUrl のクリーンアップ（アンマウント時） */
  useEffect(() => {
    return () => {
      pendingImages.forEach((p) => {
        if (p?.previewUrl) URL.revokeObjectURL(p.previewUrl);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: "flex",
        flexDirection: "column",
        // 動的測定の結果を使う。未計測時は dvh ベースのフォールバック
        height: containerHeight
          ? `${containerHeight}px`
          : { xs: "60dvh", sm: "calc(100dvh - 360px)" },
        minHeight: 320,
        borderRadius: 2.5,
        border: `1px solid ${border}`,
        bgcolor: surface,
        overflow: "hidden",
      }}
    >
      {/* メッセージ一覧 */}
      <Box
        ref={listRef}
        onScroll={onListScroll}
        onClick={() => setTappedActionsId(null)}
        sx={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          px: { xs: 1.25, sm: 2 },
          py: 1.25,
        }}
      >
        {loadingInitial ? (
          <Box sx={{ textAlign: "center", py: 6 }}>
            <CircularProgress size={22} sx={{ color: accent }} />
          </Box>
        ) : allMessages.length === 0 ? (
          <Box
            sx={{
              textAlign: "center",
              py: 6,
              color: textBase,
              opacity: 0.55,
            }}
          >
            <ChatBubbleOutlineRoundedIcon sx={{ fontSize: 32, mb: 1 }} />
            <Typography variant="body2">最初のメッセージを送ろう</Typography>
          </Box>
        ) : (
          <>
            {loadingMore && (
              <Box sx={{ textAlign: "center", py: 1 }}>
                <CircularProgress size={18} sx={{ color: accent }} />
              </Box>
            )}
            {!hasMore && allMessages.length > 0 && (
              <Typography
                variant="caption"
                sx={{
                  display: "block",
                  textAlign: "center",
                  color: textBase,
                  opacity: 0.45,
                  py: 1,
                }}
              >
                これ以上前のメッセージはありません
              </Typography>
            )}
            {allMessages.map((m, i) => {
              const prev = allMessages[i - 1];
              let isConsecutive = false;
              if (prev && !prev.deleted && !m.deleted && prev.uid === m.uid) {
                const a = prev.createdAt?.toMillis?.() || 0;
                const b = m.createdAt?.toMillis?.() || 0;
                if (a && b && b - a < CONSECUTIVE_GAP_MS) isConsecutive = true;
              }
              return (
                <MessageBubble
                  key={m.id}
                  msg={m}
                  members={members}
                  currentUid={currentUid}
                  isAdmin={isAdmin}
                  accent={accent}
                  textBase={textBase}
                  border={border}
                  surface={surface}
                  onReply={handleReply}
                  onToggleLike={handleToggleLike}
                  onDelete={handleDelete}
                  onOpenImage={handleOpenImage}
                  onImageLoaded={handleImageLoaded}
                  tappedActions={tappedActionsId === m.id}
                  onToggleTap={handleToggleTap}
                  isConsecutive={isConsecutive}
                />
              );
            })}
          </>
        )}
      </Box>

      {/* 返信プレビュー */}
      {replyTo && (
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            borderTop: `1px solid ${border}`,
            bgcolor: `${accent}08`,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <ReplyRoundedIcon sx={{ fontSize: 16, color: accent }} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: accent, fontWeight: 700 }}>
              {replyTo.name} に返信中
            </Typography>
            <Typography
              variant="caption"
              component="div"
              sx={{
                color: textBase,
                opacity: 0.75,
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {(replyTo.text || "").slice(0, 80)}
            </Typography>
          </Box>
          <IconButton size="small" onClick={() => setReplyTo(null)}>
            <CloseRoundedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}

      {/* 画像プレビュー / 処理中 / エラー */}
      {(pendingImages.length > 0 || processingImage || imageError) && (
        <Box
          sx={{
            px: 1.5,
            py: 0.75,
            borderTop: `1px solid ${border}`,
            bgcolor: `${accent}06`,
            display: "flex",
            alignItems: "center",
            gap: 1,
            flexWrap: "wrap",
          }}
        >
          {pendingImages.map((p, idx) => (
            <Box
              key={idx}
              sx={{
                position: "relative",
                width: 44,
                height: 44,
                borderRadius: 1,
                overflow: "visible",
              }}
            >
              <Box
                component="img"
                src={p.previewUrl}
                alt=""
                sx={{
                  width: 44,
                  height: 44,
                  objectFit: "cover",
                  borderRadius: 1,
                  border: `1px solid ${border}`,
                  display: "block",
                }}
              />
              <IconButton
                size="small"
                onClick={() => removePendingImage(idx)}
                sx={{
                  position: "absolute",
                  top: -8,
                  right: -8,
                  p: 0.25,
                  bgcolor: surface,
                  border: `1px solid ${border}`,
                  "&:hover": { bgcolor: surface },
                }}
              >
                <CloseRoundedIcon sx={{ fontSize: 12 }} />
              </IconButton>
            </Box>
          ))}
          {processingImage && (
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <CircularProgress size={14} sx={{ color: accent }} />
              <Typography variant="caption" sx={{ color: textBase, opacity: 0.75 }}>
                画像を準備中…
              </Typography>
            </Stack>
          )}
          {pendingImages.length > 0 && !processingImage && (
            <Typography variant="caption" sx={{ color: textBase, opacity: 0.6, ml: 0.5 }}>
              {pendingImages.length} / {IMAGES_PER_MESSAGE}
            </Typography>
          )}
          {imageError && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.5}
              sx={{ flex: 1, minWidth: 0, ml: "auto" }}
            >
              <Typography variant="caption" sx={{ color: "#c62828", flex: 1 }}>
                {imageError}
              </Typography>
              <IconButton size="small" onClick={() => setImageError("")}>
                <CloseRoundedIcon sx={{ fontSize: 14 }} />
              </IconButton>
            </Stack>
          )}
        </Box>
      )}

      {/* 入力欄 */}
      <Box
        sx={{
          borderTop: `1px solid ${border}`,
          p: 1,
          display: "flex",
          alignItems: "center",
          gap: 0.5,
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/heic,image/heif,.heic,.heif"
          multiple
          style={{ display: "none" }}
          onChange={handleFileChange}
        />
        <Tooltip title={`画像を添付（最大${IMAGES_PER_MESSAGE}枚）`}>
          <span>
            <IconButton
              onClick={handlePickFile}
              disabled={
                !currentUid ||
                sending ||
                processingImage ||
                pendingImages.length >= IMAGES_PER_MESSAGE
              }
              sx={{ color: accent, p: 0.5 }}
            >
              <AddPhotoAlternateRoundedIcon sx={{ fontSize: 22 }} />
            </IconButton>
          </span>
        </Tooltip>
        <TextField
          ref={inputRef}
          value={text}
          onChange={handleTextChange}
          onKeyDown={handleKeyDown}
          placeholder="メッセージを入力"
          multiline
          maxRows={4}
          fullWidth
          size="small"
          disabled={!currentUid || sending}
          inputProps={{ maxLength: MAX_TEXT_LEN }}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              fontSize: 14,
            },
          }}
        />
        <IconButton
          onClick={handleSend}
          disabled={
            (!text.trim() && pendingImages.length === 0) ||
            sending ||
            processingImage ||
            !currentUid
          }
          sx={{
            bgcolor: accent,
            color: "#fff",
            "&:hover": { bgcolor: accent, opacity: 0.85 },
            "&.Mui-disabled": { bgcolor: `${accent}55`, color: "#fff" },
          }}
        >
          {sending ? (
            <CircularProgress size={18} sx={{ color: "#fff" }} />
          ) : (
            <SendRoundedIcon sx={{ fontSize: 20 }} />
          )}
        </IconButton>
      </Box>

      {/* メンション候補 */}
      <Popper
        open={Boolean(mentionAnchor) && mentionCandidates.length > 0}
        anchorEl={mentionAnchor}
        placement="top-start"
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setMentionAnchor(null)}>
          <Paper
            elevation={4}
            sx={{ maxHeight: 220, overflowY: "auto", minWidth: 220 }}
          >
            {mentionCandidates.map((m) => (
              <MenuItem
                key={m.uid}
                onClick={() => insertMention(m)}
                sx={{ fontSize: 14, py: 0.75 }}
              >
                <Avatar
                  src={m.avatar || undefined}
                  sx={{ width: 24, height: 24, mr: 1, fontSize: 12, bgcolor: `${accent}20` }}
                >
                  {(m.name || "?").slice(0, 1)}
                </Avatar>
                {m.name}
              </MenuItem>
            ))}
          </Paper>
        </ClickAwayListener>
      </Popper>

      {/* ライトボックス */}
      <Lightbox
        open={lightboxIndex >= 0}
        close={() => setLightboxIndex(-1)}
        index={Math.max(0, lightboxIndex)}
        slides={imageSlides}
        on={{
          view: ({ index }) => setLightboxIndex(index),
        }}
        toolbar={{
          buttons: [
            (() => {
              const slide =
                lightboxIndex >= 0 ? imageSlides[lightboxIndex] : null;
              const canDelete =
                slide && (slide.uid === currentUid || isAdmin);
              if (!canDelete) return null;
              return (
                <button
                  key="delete"
                  type="button"
                  className="yarl__button"
                  onClick={() => handleDeleteImageAt(lightboxIndex)}
                  aria-label="この画像を削除"
                  style={{ color: "#fff" }}
                >
                  <DeleteOutlineRoundedIcon style={{ fontSize: 22 }} />
                </button>
              );
            })(),
            "close",
          ].filter(Boolean),
        }}
      />
    </Box>
  );
}
